import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Drawer,
  Stack,
  Typography,
  TextField,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { useSelector } from "react-redux";
import { format, addDays, parseISO, isBefore, isAfter } from "date-fns";
import LockIcon from "@mui/icons-material/Lock";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import useApi from "../../hooks/useApi";
import usePage from "../../hooks/usePage";
import useSupervisorReps from "../../hooks/useSupervisorReps";
import useCall from "../../hooks/useCall";
import useLocalization from "../../hooks/useLocalization";
import CallItem from "./CallItem";
import CallViewModal from "../modals/CallViewModal";
import CallDocketModal from "../modals/CallDocketModal";

const CallsPanel = ({
  date,
  calls,
  onClose,
  onRefresh,
  isSupervisor = false,
  reps = [],
  supervisorId = null,
}) => {
  const [selectedCalls, setSelectedCalls] = useState({});
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleType, setRescheduleType] = useState("absolute"); // "absolute" or "relative"
  const [relativeDays, setRelativeDays] = useState("");
  const [reassignRep, setReassignRep] = useState(null); // Selected rep object
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [selectedCallForDocket, setSelectedCallForDocket] = useState(null);
  const [expandedLocations, setExpandedLocations] = useState({}); // Track expanded state by unique location key
  const [resultDialog, setResultDialog] = useState({
    open: false,
    type: "success",
    message: "",
    errors: [],
  });
  const { postData } = useApi();
  const { showMessage } = usePage();
  const { reps: fetchedReps } = useSupervisorReps(supervisorId);
  const { getCallById } = useCall();
  const { getLabel, getMessage, getMonthFullName } = useLocalization();
  const language = useSelector((state) => state.authentication.language);
  const isFrench = String(language || "")
    .toLowerCase()
    .startsWith("fr");
  const unknownLabel = getLabel("unknown");
  const unknownLocationLabel = `${unknownLabel} ${getLabel("location")}`;

  const getLocalizedName = (entity, fallbackName, fallbackFrenchName) => {
    const defaultName = fallbackName || "";
    if (!entity || typeof entity !== "object") {
      return isFrench
        ? fallbackFrenchName || defaultName
        : defaultName || fallbackFrenchName || "";
    }

    if (isFrench) {
      return (
        entity.name_fr || fallbackFrenchName || entity.name || defaultName || ""
      );
    }

    return (
      entity.name || defaultName || entity.name_fr || fallbackFrenchName || ""
    );
  };

  const formatMessage = (key, replacements = {}) => {
    let message = getMessage(key);
    if (!message) return "";
    Object.entries(replacements).forEach(([token, value]) => {
      message = message.split(`{${token}}`).join(value);
    });
    return message;
  };

  // Debug: Track resultDialog state changes
  useEffect(() => {}, [resultDialog]);

  // Group calls by rep (supervisors) then by location (both)
  const groupedByRep = {};
  const groupedByLocation = {};

  if (isSupervisor && Array.isArray(calls)) {
    // Supervisor view: Group by rep, then by location within each rep
    calls.forEach((call) => {
      const repId = call.repId || call.userId;
      const repName =
        call.repName ||
        call.userInfo?.legalName ||
        call.userInfo?.name ||
        unknownLabel;
      const locationId =
        call.locationId || call.location?._id || call.location?.id;
      const locationName =
        call.location?.name || call.locationName || unknownLocationLabel;
      const locationNumber =
        call.locationNumber || call.location?.locationNumber;
      const address = call.location?.address || {};

      if (!groupedByRep[repId]) {
        groupedByRep[repId] = {
          repName,
          locations: {},
        };
      }

      if (!groupedByRep[repId].locations[locationId]) {
        groupedByRep[repId].locations[locationId] = {
          locationName,
          locationNumber,
          address,
          calls: [],
        };
      }

      groupedByRep[repId].locations[locationId].calls.push(call);
    });
  } else if (Array.isArray(calls)) {
    // Rep view: Group by location only
    calls.forEach((call) => {
      const locationId =
        call.locationId || call.location?._id || call.location?.id;
      const locationName =
        call.location?.name || call.locationName || unknownLocationLabel;
      const locationNumber =
        call.locationNumber || call.location?.locationNumber;
      const address = call.location?.address || {};

      if (!groupedByLocation[locationId]) {
        groupedByLocation[locationId] = {
          locationName,
          locationNumber,
          address,
          calls: [],
        };
      }

      groupedByLocation[locationId].calls.push(call);
    });
  }

  // Determine which calls can be rescheduled
  const moveableCallIds = calls
    .filter((call) => call.callStatus !== "complete")
    .map((call) => call.id);

  const handleToggleCall = (callId) => {
    setSelectedCalls((prev) => ({
      ...prev,
      [callId]: !prev[callId],
    }));
  };

  const handleSelectAll = () => {
    const newSelection = {};
    moveableCallIds.forEach((id) => {
      newSelection[id] = true;
    });
    setSelectedCalls(newSelection);
  };

  const handleClearSelection = () => {
    setSelectedCalls({});
  };

  const getSelectedCount = () => {
    return Object.values(selectedCalls).filter(Boolean).length;
  };

  const toDateOnly = (value) => {
    if (!value) return null;

    let parsed;
    if (value instanceof Date) {
      parsed = new Date(value);
    } else if (typeof value === "string") {
      parsed = parseISO(value);
    } else {
      parsed = new Date(value);
    }

    if (isNaN(parsed.getTime())) return null;
    parsed.setHours(0, 0, 0, 0);
    return parsed;
  };

  const getProjectRange = (call) => {
    const projectStartRaw =
      call.projectStartDate ||
      call.project?.startsOn ||
      call.project?.yyyymmddStart;
    const projectEndRaw =
      call.projectEndDate || call.project?.endsOn || call.project?.yyyymmddEnd;

    return {
      start: toDateOnly(projectStartRaw),
      end: toDateOnly(projectEndRaw),
    };
  };

  const getCallTargetDate = (call) => {
    if (rescheduleType === "absolute") {
      return toDateOnly(rescheduleDate);
    }

    const offsetDays = Number(relativeDays);
    if (!Number.isFinite(offsetDays)) return null;

    const baseDate = toDateOnly(call.yyyymmdd || call.scheduleDate);
    if (!baseDate) return null;

    return addDays(baseDate, offsetDays);
  };

  const getProjectDateConflicts = () => {
    const selectedCallIds = Object.keys(selectedCalls).filter(
      (id) => selectedCalls[id],
    );

    if (selectedCallIds.length === 0) return [];

    const hasRescheduleInput =
      (rescheduleType === "absolute" && !!rescheduleDate) ||
      (rescheduleType === "relative" && relativeDays !== "");
    if (!hasRescheduleInput) return [];

    const selectedCallSet = new Set(selectedCallIds);
    const selectedMoveableCalls = calls.filter(
      (call) => selectedCallSet.has(call.id) && call.callStatus !== "complete",
    );

    const conflicts = [];
    for (const call of selectedMoveableCalls) {
      const targetDate = getCallTargetDate(call);
      if (!targetDate) continue;

      const { start, end } = getProjectRange(call);
      if (start && isBefore(targetDate, start)) {
        conflicts.push({
          callId: call.id,
          projectName:
            getLocalizedName(
              call.project,
              call.projectName,
              call.projectName_fr,
            ) || unknownLabel,
          target: format(targetDate, "yyyy-MM-dd"),
          range: `${format(start, "yyyy-MM-dd")}+`,
        });
        continue;
      }

      if (end && isAfter(targetDate, end)) {
        conflicts.push({
          callId: call.id,
          projectName:
            getLocalizedName(
              call.project,
              call.projectName,
              call.projectName_fr,
            ) || unknownLabel,
          target: format(targetDate, "yyyy-MM-dd"),
          range: `<=${format(end, "yyyy-MM-dd")}`,
        });
      }
    }

    return conflicts;
  };

  const projectDateConflicts = getProjectDateConflicts();
  const hasProjectDateConflicts = projectDateConflicts.length > 0;

  const toggleLocation = (locationKey) => {
    setExpandedLocations((prev) => ({
      ...prev,
      [locationKey]: !prev[locationKey],
    }));
  };

  const handleOpenDocket = async (call) => {
    // Fetch full call data with all joins (project, location, surveys, etc.)
    const callId = call.id || call._id;

    // Try getCallById first (uses pipeline: "full" with id parameter)
    const fullCallData = await getCallById(callId);

    if (fullCallData) {
      setSelectedCallForDocket(fullCallData);
    } else {
      console.error("[CallsPanel] Failed to load call data for ID:", callId);
      showMessage(getMessage("unableToLoadCallDetails"));
    }
  };

  const handleCloseDocket = () => {
    setSelectedCallForDocket(null);
  };

  const handleDocketRefresh = async () => {
    // Refresh the full call data in the modal
    if (selectedCallForDocket) {
      const callId = selectedCallForDocket.id || selectedCallForDocket._id;
      const fullCallData = await getCallById(callId);
      if (fullCallData) {
        setSelectedCallForDocket(fullCallData);
      }
    }
    // Also refresh the parent list
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleReschedule = async () => {
    const callIds = Object.keys(selectedCalls).filter(
      (id) => selectedCalls[id],
    );
    if (callIds.length === 0) {
      showMessage(getMessage("selectAtLeastOneCall"));
      return;
    }

    // Validate that at least one action is selected (reschedule OR reassign)
    const hasRescheduleData =
      (rescheduleType === "absolute" && rescheduleDate) ||
      (rescheduleType === "relative" && relativeDays);
    const hasReassignment = !!reassignRep;

    if (!hasRescheduleData && !hasReassignment) {
      showMessage(getMessage("selectRescheduleOrReassign"));
      return;
    }

    if (hasRescheduleData && hasProjectDateConflicts) {
      const sample = projectDateConflicts
        .slice(0, 3)
        .map((conflict) => `${conflict.projectName} (${conflict.target})`)
        .join(", ");

      showMessage(
        `Cannot apply changes: ${projectDateConflicts.length} selected call(s) are outside project date range. ${sample}`,
      );
      return;
    }

    setIsRescheduling(true);
    try {
      const data = {
        callIds,
      };

      // Add reschedule data if applicable
      if (hasRescheduleData) {
        data.rescheduleData =
          rescheduleType === "absolute"
            ? {
                type: "absolute",
                value: rescheduleDate,
              }
            : {
                type: "relative",
                value: parseInt(relativeDays, 10),
              };
      }

      // Add reassignment data if applicable
      if (hasReassignment) {
        data.newRepId = reassignRep._id || reassignRep.id;
      }

      const result = await postData({
        entityName: "Call",
        action: "bulkReschedule",
        data,
      });

      // Check if result is successful (could be result.isSuccessful or just result with data)
      const isSuccessful = result?.isSuccessful !== false; // Treat undefined as success if data exists
      const resultData = result?.data || result || {};
      const { successCount = 0, totalCount = 0, errors = [] } = resultData;

      const actions = [];
      if (hasRescheduleData) actions.push(getLabel("rescheduled"));
      if (hasReassignment) actions.push(getLabel("reassigned"));
      const actionsLabel = actions.join(` ${getLabel("and")} `);

      // Show result in dialog based on outcome
      if (errors.length === 0 && successCount > 0) {
        // All succeeded
        setResultDialog({
          open: true,
          type: "success",
          message: formatMessage("allCallsActionSuccess", {
            count: successCount,
            actions: actionsLabel,
          }),
          errors: [],
        });
        // Refresh after success
        onRefresh();
        setRescheduleDate("");
        setRelativeDays("");
        setReassignRep(null);
      } else if (successCount > 0 && errors.length > 0) {
        // Partial success
        setResultDialog({
          open: true,
          type: "warning",
          message: formatMessage("partialCallsActionSuccess", {
            successCount,
            totalCount,
            actions: actionsLabel,
            errorCount: errors.length,
          }),
          errors: errors,
        });
        // Refresh after partial success
        onRefresh();
        setRescheduleDate("");
        setRelativeDays("");
        setReassignRep(null);
      } else if (errors.length > 0) {
        // All failed - DON'T refresh, let user see the error first
        setResultDialog({
          open: true,
          type: "error",
          message: formatMessage("allCallsActionFailed", {
            totalCount,
            actions: actionsLabel,
          }),
          errors: errors,
        });
      } else {
        // Unexpected state
        const serverMessage = result?.message ? getMessage(result.message) : "";
        setResultDialog({
          open: true,
          type: "error",
          message: serverMessage || getMessage("unexpectedResult"),
          errors: [],
        });
      }
    } catch (error) {
      setResultDialog({
        open: true,
        type: "error",
        message: formatMessage("errorProcessingCalls", {
          error: error.message,
        }),
        errors: [],
      });
    } finally {
      setIsRescheduling(false);
    }
  };

  const headerDate = new Date(date);
  const monthLabel =
    getMonthFullName(headerDate.getMonth()) || format(headerDate, "MMMM");
  const formattedDate = `${monthLabel} ${format(headerDate, "d, yyyy")}`;

  return (
    <>
      <Drawer
        anchor="right"
        open={true}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: "45%",
            maxWidth: 600,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            padding: 2,
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            {formattedDate} - {calls.length} {getLabel("calls")}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Calls List */}
        <Box sx={{ flex: 1, overflowY: "auto", padding: 1 }}>
          <Stack spacing={isSupervisor ? 2 : 1}>
            {isSupervisor
              ? Object.entries(groupedByRep).map(([repId, repGroup]) => (
                  <Box key={repId}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        padding: 1,
                        backgroundColor: "#1976d2",
                        borderRadius: 1,
                        color: "#fff",
                      }}
                    >
                      {repGroup.repName}
                    </Typography>
                    <Stack
                      spacing={1}
                      sx={{ marginTop: 1 }}
                    >
                      {Object.entries(repGroup.locations).map(
                        ([locationId, locationGroup]) => {
                          const locationKey = `${repId}-${locationId}`;
                          const isExpanded =
                            expandedLocations[locationKey] !== false; // Default to expanded

                          return (
                            <Box key={locationId}>
                              <Box
                                onClick={() => toggleLocation(locationKey)}
                                sx={{
                                  padding: 0.75,
                                  paddingLeft: 2,
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: 1,
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  "&:hover": {
                                    backgroundColor: "#eeeeee",
                                  },
                                }}
                              >
                                {isExpanded ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      color: "#333",
                                    }}
                                  >
                                    {locationGroup.locationNumber &&
                                      `#${locationGroup.locationNumber} - `}
                                    {locationGroup.locationName}
                                  </Typography>
                                  {locationGroup.address && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#666",
                                        display: "block",
                                        marginTop: 0.25,
                                      }}
                                    >
                                      {locationGroup.address.streetAddress &&
                                        `${locationGroup.address.streetAddress}, `}
                                      {locationGroup.address.city &&
                                        `${locationGroup.address.city}, `}
                                      {locationGroup.address.province}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              {isExpanded && (
                                <Stack
                                  spacing={1}
                                  sx={{ marginLeft: 2, marginTop: 0.5 }}
                                >
                                  {locationGroup.calls.map((call) => {
                                    const canMove =
                                      call.callStatus !== "complete";
                                    const isSelected = selectedCalls[call.id];

                                    return (
                                      <Box
                                        key={call.id}
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          padding: 1.5,
                                          border: "1px solid #e0e0e0",
                                          borderRadius: 1,
                                          backgroundColor: isSelected
                                            ? "#f5f5f5"
                                            : "#fff",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            minWidth: 24,
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          {canMove ? (
                                            <Checkbox
                                              checked={isSelected || false}
                                              onChange={() =>
                                                handleToggleCall(call.id)
                                              }
                                              size="small"
                                            />
                                          ) : (
                                            <LockIcon
                                              sx={{
                                                fontSize: "1.25rem",
                                                color: "#999",
                                              }}
                                            />
                                          )}
                                        </Box>
                                        <CallItem
                                          call={call}
                                          onView={handleOpenDocket}
                                        />
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              )}
                            </Box>
                          );
                        },
                      )}
                    </Stack>
                  </Box>
                ))
              : Object.entries(groupedByLocation).map(
                  ([locationId, locationGroup]) => {
                    const isExpanded = expandedLocations[locationId] !== false; // Default to expanded

                    return (
                      <Box key={locationId}>
                        <Box
                          onClick={() => toggleLocation(locationId)}
                          sx={{
                            padding: 0.75,
                            backgroundColor: "#f5f5f5",
                            borderRadius: 1,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            "&:hover": {
                              backgroundColor: "#eeeeee",
                            },
                          }}
                        >
                          {isExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: "#333",
                              }}
                            >
                              {locationGroup.locationNumber &&
                                `#${locationGroup.locationNumber} - `}
                              {locationGroup.locationName}
                            </Typography>
                            {locationGroup.address && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#666",
                                  display: "block",
                                  marginTop: 0.25,
                                }}
                              >
                                {locationGroup.address.streetAddress &&
                                  `${locationGroup.address.streetAddress}, `}
                                {locationGroup.address.city &&
                                  `${locationGroup.address.city}, `}
                                {locationGroup.address.province}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {isExpanded && (
                          <Stack
                            spacing={1}
                            sx={{ marginTop: 0.5 }}
                          >
                            {locationGroup.calls.map((call) => {
                              const canMove = call.callStatus !== "complete";
                              const isSelected = selectedCalls[call.id];

                              return (
                                <Box
                                  key={call.id}
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    padding: 1.5,
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 1,
                                    backgroundColor: isSelected
                                      ? "#f5f5f5"
                                      : "#fff",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: 24,
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    {canMove ? (
                                      <Checkbox
                                        checked={isSelected || false}
                                        onChange={() =>
                                          handleToggleCall(call.id)
                                        }
                                        size="small"
                                      />
                                    ) : (
                                      <LockIcon
                                        sx={{
                                          fontSize: "1.25rem",
                                          color: "#999",
                                        }}
                                      />
                                    )}
                                  </Box>
                                  <CallItem
                                    call={call}
                                    onView={handleOpenDocket}
                                  />
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    );
                  },
                )}
          </Stack>
        </Box>

        <Divider />

        {/* Fixed Footer with Date Picker and Actions */}
        <Box sx={{ padding: 2, borderTop: "1px solid #e0e0e0" }}>
          <Stack spacing={2}>
            {/* Reschedule Type Selection */}
            <Box>
              <Typography
                variant="body2"
                sx={{ marginBottom: 1, fontWeight: 600 }}
              >
                {getLabel("rescheduleType")}:
              </Typography>
              <RadioGroup
                row
                value={rescheduleType}
                onChange={(e) => setRescheduleType(e.target.value)}
              >
                <FormControlLabel
                  value="absolute"
                  control={<Radio size="small" />}
                  label={getLabel("setToSpecificDate")}
                />
                <FormControlLabel
                  value="relative"
                  control={<Radio size="small" />}
                  label={getLabel("relativeDays")}
                />
              </RadioGroup>
            </Box>

            {/* Reschedule Options */}
            {rescheduleType === "absolute" ? (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ marginBottom: 1, fontWeight: 600 }}
                >
                  {getLabel("rescheduleTo")}
                </Typography>
                <TextField
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  inputProps={{
                    min: format(new Date(), "yyyy-MM-dd"),
                  }}
                  fullWidth
                  size="small"
                  disabled={isRescheduling}
                />
              </Box>
            ) : (
              <Box>
                <Typography
                  variant="body2"
                  sx={{ marginBottom: 1, fontWeight: 600 }}
                >
                  {getLabel("adjustByDays")}
                </Typography>
                <TextField
                  type="number"
                  value={relativeDays}
                  onChange={(e) => setRelativeDays(e.target.value)}
                  placeholder={getLabel("relativeDaysPlaceholder")}
                  fullWidth
                  size="small"
                  disabled={isRescheduling}
                  helperText={getLabel("adjustDaysHelperText")}
                />
              </Box>
            )}

            {hasProjectDateConflicts && (
              <Alert severity="warning">
                {projectDateConflicts.length} selected call(s) fall outside
                project start/end dates for this reschedule input.
              </Alert>
            )}

            {/* Rep Reassignment (Optional) */}
            {isSupervisor && (
              <Autocomplete
                options={fetchedReps || []}
                value={reassignRep}
                onChange={(event, newValue) => setReassignRep(newValue)}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  (option._id || option.id) === (value._id || value.id)
                }
                disabled={isRescheduling}
                slotProps={{
                  paper: {
                    sx: { maxHeight: "300px" },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={getLabel("reassignRepOptional")}
                    size="small"
                  />
                )}
                noOptionsText={getLabel("noRepsAvailable")}
              />
            )}

            {/* Selection Summary */}
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Typography variant="body2">
                  {getSelectedCount()} {getLabel("of")} {moveableCallIds.length}{" "}
                  {getLabel("moveable")}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleSelectAll}
                  disabled={moveableCallIds.length === 0}
                >
                  {getLabel("selectAll")}
                </Button>
                {getSelectedCount() > 0 && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleClearSelection}
                  >
                    {getLabel("clear")}
                  </Button>
                )}
              </Stack>
            </Box>

            {/* Action Buttons */}
            <Stack
              direction="row"
              spacing={1}
            >
              <Button
                variant="contained"
                onClick={handleReschedule}
                disabled={
                  isRescheduling ||
                  getSelectedCount() === 0 ||
                  (hasProjectDateConflicts &&
                    ((rescheduleType === "absolute" && !!rescheduleDate) ||
                      (rescheduleType === "relative" &&
                        relativeDays !== ""))) ||
                  (!reassignRep &&
                    ((rescheduleType === "absolute" && !rescheduleDate) ||
                      (rescheduleType === "relative" && !relativeDays)))
                }
                fullWidth
              >
                {isRescheduling ? (
                  <CircularProgress size={20} />
                ) : (
                  getLabel("applyChanges")
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={onClose}
                fullWidth
              >
                {getLabel("cancel")}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>

      {/* Call Docket/View Modal - Supervisors get full edit, Reps get view-only */}
      {isSupervisor ? (
        <CallDocketModal
          call={selectedCallForDocket}
          open={!!selectedCallForDocket}
          onClose={handleCloseDocket}
          onRefresh={onRefresh}
        />
      ) : (
        <CallViewModal
          call={selectedCallForDocket}
          open={!!selectedCallForDocket}
          onClose={handleCloseDocket}
        />
      )}

      {/* Result Dialog */}
      <Dialog
        open={resultDialog.open}
        onClose={() => setResultDialog({ ...resultDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {resultDialog.type === "success"
            ? getLabel("success")
            : resultDialog.type === "warning"
              ? getLabel("partialSuccess")
              : getLabel("error")}
        </DialogTitle>
        <DialogContent>
          <Alert
            severity={resultDialog.type}
            sx={{ marginBottom: 2 }}
          >
            {resultDialog.message}
          </Alert>
          {resultDialog.errors.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ marginBottom: 1, fontWeight: 600 }}
              >
                {getLabel("failedCalls")}
              </Typography>
              <Stack spacing={1}>
                {resultDialog.errors.map((err, idx) => {
                  // Find the call details from the calls array
                  const call = calls.find(
                    (c) => c.id === err.callId || c._id === err.callId,
                  );
                  const locationName =
                    call?.location?.name ||
                    call?.locationName ||
                    unknownLocationLabel;
                  const locationNumber =
                    call?.locationNumber || call?.location?.locationNumber;
                  const address =
                    call?.location?.address || call?.address || {};
                  const addressStr = [
                    address.streetAddress,
                    address.city,
                    address.province,
                  ]
                    .filter(Boolean)
                    .join(", ");
                  const projectName =
                    getLocalizedName(
                      call?.project,
                      call?.projectName,
                      call?.projectName_fr,
                    ) || `${unknownLabel} ${getLabel("project")}`;

                  return (
                    <Alert
                      key={idx}
                      severity="error"
                      sx={{ fontSize: "0.875rem" }}
                    >
                      <strong>{getLabel("project")}:</strong> {projectName}
                      <br />
                      <strong>{getLabel("location")}:</strong>{" "}
                      {locationNumber && `#${locationNumber} - `}
                      {locationName}
                      {addressStr && (
                        <>
                          <br />
                          <strong>{getLabel("address")}:</strong> {addressStr}
                        </>
                      )}
                      <br />
                      <strong>{getLabel("reason")}:</strong> {err.error}
                    </Alert>
                  );
                })}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setResultDialog({ ...resultDialog, open: false })}
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CallsPanel;
